import { Outlet, Link, useNavigate, useLocation as useRouterLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { motion } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useState, useEffect } from 'react';

// Layout sub-components
import { DesktopNav } from '@/components/layout/DesktopNav';
import { MobileNav } from '@/components/layout/MobileNav';
import { Footer } from '@/components/layout/Footer';

// Floating/utility components
import ChatbotWidget from '@/components/ChatbotWidget';
import MusicPlayer from '@/components/MusicPlayer';
import BackButton from '@/components/BackButton';
import TickerFeed from '@/components/TickerFeed';
import QuickNav from '@/components/QuickNav';
import StickyHelpButton from '@/components/StickyHelpButton';
import ExitIntentPopup from '@/components/ExitIntentPopup';
import GettingStartedChecklist from '@/components/GettingStartedChecklist';
import MobileBottomNav from '@/components/MobileBottomNav';
import PushNotificationOptIn from '@/components/PushNotificationOptIn';
import MobileCTA from '@/components/MobileCTA';
import PinPageButton from '@/components/PinPageButton';
import SiteJsonLd from '@/components/SiteJsonLd';
import NextBestStepCard from '@/components/NextBestStepCard';

function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const routerLocation = useRouterLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Scroll to top when route changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [routerLocation.pathname]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isAdminPage = routerLocation.pathname.startsWith('/admin');

  // Routes where the user is mid-task and an exit-intent overlay would be
  // disruptive. Keep this list focused on in-flow pages, not browse pages.
  const _IN_FLOW_PREFIXES = [
    '/proof/',
    '/pitch-practice',
    '/mental-health/audit',
    '/grants/tracker',
    '/grant-concierge',
    '/skills-barter',
    '/next-step',
    '/sop-builder',
    '/business-builder',
  ];
  const isInFlowPage = (path) => _IN_FLOW_PREFIXES.some((p) => path === p || path.startsWith(p));

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Site-wide Organization JSON-LD (NGO schema) — credibility / SEO */}
      <SiteJsonLd />
      {/* Skip to Content Link for Accessibility */}
      <a 
        href="#main-content" 
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[9999] focus:bg-[#006847] focus:text-white focus:px-4 focus:py-2 focus:rounded-md focus:outline-none"
      >
        Skip to main content
      </a>
      
      {/* Ticker Feed */}
      <TickerFeed />
      
      {/* Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50 border-b border-gray-200">
        <nav className="w-full px-4 lg:px-8 xl:px-12">
          <div className="flex items-center gap-4 lg:gap-6 h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center space-x-2 flex-shrink-0">
              <img 
                src="https://customer-assets.emergentagent.com/job_equity-focus/artifacts/875dlk3n_dowurk_color_shield.png" 
                alt="DowUrk Logo" 
                className="h-8 w-8 object-contain"
              />
              <span className="text-base lg:text-lg font-bold bg-gradient-to-r from-[#8BC34A] to-[#388E3C] bg-clip-text text-transparent whitespace-nowrap">
                DowUrk<sup className="text-[8px] lg:text-[10px]">®</sup> FramewUrk
              </span>
            </Link>

            {/* Desktop Navigation */}
            <DesktopNav user={user} onLogout={handleLogout} />

            {/* Mobile Menu Button */}
            <button
              className="min-[1800px]:hidden ml-auto p-2 rounded-md text-gray-700 hover:bg-gray-100"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Navigation */}
          <MobileNav 
            isOpen={mobileMenuOpen} 
            onClose={() => setMobileMenuOpen(false)} 
            onLogout={handleLogout}
          />
        </nav>
      </header>

      {/* Main Content */}
      <main id="main-content" className="container mx-auto px-4 sm:px-6 lg:px-8 py-8 mt-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
        >
          <Outlet />
        </motion.div>
      </main>

      {/* Footer */}
      <Footer />
      
      {/* Floating Components */}
      <ChatbotWidget />
      <MusicPlayer />
      <QuickNav />

      {/* Conditional Components - hide on admin pages and on in-flow pages
          where the exit-intent overlay would interrupt the user mid-task. */}
      {routerLocation.pathname !== '/' && <BackButton />}
      {!isAdminPage && <StickyHelpButton />}
      {!isAdminPage && !isInFlowPage(routerLocation.pathname) && <ExitIntentPopup />}
      {!isAdminPage && <GettingStartedChecklist />}
      {!isAdminPage && <PushNotificationOptIn />}
      
      {/* Mobile Bottom Navigation */}
      <MobileBottomNav />
      
      {/* Mobile-First CTA Bar */}
      <MobileCTA />

      {/* Persistent "Your next step" card for logged-in users */}
      {user && <NextBestStepCard />}

      {/* Floating Pin-this-page button (mobile only) */}
      {!isAdminPage && <PinPageButton drawerOpen={mobileMenuOpen} />}
    </div>
  );
}

export default Layout;
